import { useCallback, useMemo } from 'react';
import { useExcelExport } from './useExcelExport';
import type { ExportSurveyFilters } from '../services/exportRequests.service';

export interface ExportSurveysArgs {
    surveyIds: string[] | null;
    filters?: ExportSurveyFilters;
}

export interface UseExportSurveysResult {
    exporting: boolean;
    exportError: Error | undefined;
    exportSurveys: (
        surveyIds: string[] | null,
        filters?: ExportSurveyFilters,
    ) => Promise<void>;
}

export const useExportSurveys = (): UseExportSurveysResult => {
    const config = useMemo(
        () => ({
            notificationId: 'export-surveys',
            loadingTitle: 'Exporting Surveys',
            loadingMessage: 'Your export is being prepared...',
            successTitle: 'Export Successful',
            successMessage: 'Your surveys have been exported successfully.',
            failedTitle: 'Export Failed' as const,
            filenamePrefix: 'price_surveys',
            exportPath: '/export/price-surveys',
            buildBody: ({ surveyIds, filters }: ExportSurveysArgs) => ({
                surveyIds,
                filters: filters ?? undefined,
            }),
            validate: (args: ExportSurveysArgs) => {
                if (args.surveyIds?.length === 0) {
                    return 'No surveys selected. Please select at least one survey to export.';
                }
                return null;
            },
        }),
        [],
    );

    const { exporting, exportError, runExport } =
        useExcelExport<ExportSurveysArgs>(config);

    const exportSurveys = useCallback(
        async (surveyIds: string[] | null, filters?: ExportSurveyFilters) => {
            await runExport({ surveyIds, filters });
        },
        [runExport],
    );

    return { exporting, exportError, exportSurveys };
};
